import React from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "./ui/dropdown-menu";

const LANGS = [
  { code: "en", label: "English", short: "EN" },
  { code: "zh-CN", label: "简体中文", short: "中文" },
];

/**
 * Compact globe-icon dropdown for switching between English and Simplified
 * Chinese. Persists to localStorage via i18next-browser-languagedetector.
 *
 * variant:
 *   - "footer"  (default) — subtle grey link, matches the Login footer legal
 *                          links.
 *   - "header"  — pill-shaped, for the top nav bars in each portal.
 */
export default function LanguageSwitcher({ variant = "footer" }) {
  const { i18n, t } = useTranslation("common");
  const current = LANGS.find((l) => l.code === i18n.language) || LANGS[0];

  const trigger =
    variant === "header" ? (
      <button
        type="button"
        data-testid="lang-switcher-trigger"
        className="inline-flex items-center gap-1.5 rounded-full border border-[#EBE5D9] bg-white px-3 py-1.5 text-xs font-semibold text-[#4B4642] hover:border-[#2C2A29] transition-colors"
      >
        <Globe size={14} className="text-[#7A7571]" />
        <span>{current.short}</span>
      </button>
    ) : (
      <button
        type="button"
        data-testid="lang-switcher-trigger"
        className="inline-flex items-center gap-1 text-[#7A7571] hover:text-[#2C2A29] transition-colors"
      >
        <Globe size={12} />
        <span>{current.short}</span>
      </button>
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">
          {t("language.label")}
        </div>
        {LANGS.map((l) => (
          <DropdownMenuItem
            key={l.code}
            data-testid={`lang-option-${l.code}`}
            onSelect={() => i18n.changeLanguage(l.code)}
            className="flex items-center justify-between cursor-pointer"
          >
            <span>{l.label}</span>
            {i18n.language === l.code && <Check size={14} className="text-[#166534]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
